import React from 'react'

import { Field } from 'redux-form/immutable'

import PrimaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/primaryButton/PrimaryButton'
import DropZone from '@lyrahealth-inc/ui-core/lib/atoms/form/dropZone/DropZone'
import MaskedInputField from '@lyrahealth-inc/ui-core/lib/atoms/form/maskedInputField/MaskedInputField'
import { InputFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { SelectFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { IdCardIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/IdCardIcon'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './providerInfoForm.module.scss'
import { paymentPreferences, states } from '../../../constants/appConstants'
import HiddenField from '../HiddenField'

const stateOptions = states.map((state) => ({ label: state.text, value: state.value }))
const paymentPreferenceOptions = Object.entries(paymentPreferences).map(([key, val]) => ({ label: val, value: key }))

const dropZonePlaceholder = () => {
  return (
    <div className={styles['dropzone-placeholder']}>
      <IdCardIcon size={57} fillColor={styles.x_light_gray_100} />
      <span>Drag image here</span>
    </div>
  )
}

const dropZoneContext = (
  <div>Please upload a photo of your passport, driver’s license or ID card in JPEG or PNG formats</div>
)

const nameOnAccountToolTipContent = 'Check Line 1 of your Form W-9 for the owner’s name or business name.'

const doingBusinessAsToolTipContent = 'Check Line 2 of your Form W-9.'

type Props = {
  handleSubmit?: (...args: any[]) => any
  submitting?: boolean
  submitFunction?: (...args: any[]) => any
  primaryButtonText?: string
  accountType?: string
}

class ProviderInfoForm extends React.Component<Props> {
  render() {
    const { handleSubmit, submitting, submitFunction, primaryButtonText, accountType } = this.props

    return (
      // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      <form onSubmit={handleSubmit(submitFunction)}>
        <Field name='lyra_id' component={HiddenField} />
        <Field name='account_type' component={HiddenField} />

        <h3>Identity</h3>
        {accountType === 'company' && <p>Enter the information of the business’s owner or operator.</p>}
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field name='first_name' readOnly component={InputFieldRFF} label='First Name' placeholder='First Name' />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field name='last_name' readOnly component={InputFieldRFF} label='Last Name' placeholder='Last Name' />
          </div>
        </div>
        <div className='row'>
          <div className={`col-xs-12 ${accountType === 'individual' ? 'col-sm-6' : ''}`}>
            <Field name='email' readOnly component={InputFieldRFF} label='Email' placeholder='Email' />
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='phone'
              type='tel'
              component={MaskedInputField}
              mask='(111) 111-1111'
              placeholder='(XXX) XXX-XXXX'
              label='Phone Number'
              data-test-id='ProviderInfoForm-phonenumber-field'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='date_of_birth'
              type='tel'
              component={MaskedInputField}
              mask='11/11/1111'
              placeholder='MM/DD/YYYY'
              label='Date of Birth'
              data-test-id='ProviderInfoForm-dob-field'
            />
          </div>
        </div>
        <div className='row'>
          {accountType === 'company' && (
            <div className='col-xs-6'>
              <Field
                name='ssn'
                type='text'
                component={MaskedInputField}
                mask='111111111'
                label='Social Security Number'
                placeholder='XXXXXXXXX'
                data-test-id='ProviderInfoForm-company-ssn-field'
              />
            </div>
          )}
        </div>

        <div className='row'>
          <div className='col-xs-12'>
            <Field
              name='identification_file_front'
              className={styles['id-dropzone']}
              component={DropZone}
              // @ts-expect-error TS(2769): No overload matches this call.
              placeholder={dropZonePlaceholder}
              context={dropZoneContext}
              label='Please Upload Front of Your Identification Document'
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-xs-12'>
            <Field
              name='identification_file_back'
              className={styles['id-dropzone']}
              component={DropZone}
              // @ts-expect-error TS(2769): No overload matches this call.
              placeholder={dropZonePlaceholder}
              context={dropZoneContext}
              label='Please Upload Back of Your Identification Document'
            />
          </div>
        </div>

        <h3>Tax Information</h3>
        <p>
          Enter the exact tax name (Line 1), taxpayer ID number, and address from your W-9. We’ll send important tax
          documents to the address you enter.
        </p>
        {accountType === 'individual' && (
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='account_identifier'
                component={MaskedInputField}
                mask='111111111'
                placeholder='XXXXXXXXX'
                label='Social Security Number'
                data-test-id='ProviderInfoForm-individual-ssn-field'
              />
            </div>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='street'
                component={InputFieldRFF}
                label='Street Address'
                placeholder='Street Address'
                testID={tID('ProviderInfoForm-individual-address-field')}
              />
            </div>
          </div>
        )}

        {accountType === 'company' && (
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='account_identifier'
                component={InputFieldRFF}
                placeholder='9 digit number'
                label='TIN or EIN Number'
                testID={tID('ProviderInfoForm-company-tin-field')}
              />
            </div>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='street'
                component={InputFieldRFF}
                label='Street Address'
                placeholder='Street Address'
                testID={tID('ProviderInfoForm-company-address-field')}
              />
            </div>
          </div>
        )}

        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='city'
              component={InputFieldRFF}
              label='City'
              placeholder='City'
              testID={tID('ProviderInfoForm-city-field')}
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field name='state' component={SelectFieldRFF} label='State' options={stateOptions} placeholder='Select' />
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='zip_code'
              component={MaskedInputField}
              mask='11111'
              label='Zip Code'
              placeholder='Zip Code'
              data-test-id='ProviderInfoForm-zipcode-field'
            />
          </div>
          {accountType === 'company' && (
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='account_name'
                toolTipContent={nameOnAccountToolTipContent}
                component={InputFieldRFF}
                label='Name on account (for tax purposes)'
                placeholder='Name on account (for tax purposes)'
                testID={tID('ProviderInfoForm-nameonaccount-field')}
              />
            </div>
          )}
        </div>

        {accountType === 'company' && (
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='dba'
                toolTipContent={doingBusinessAsToolTipContent}
                component={InputFieldRFF}
                label='Doing Dusiness As (DBA)'
                placeholder='Doing Business As (DBA)'
                testID={tID('ProviderInfoForm-dba-field')}
              />
            </div>
          </div>
        )}

        <h3>Bank Information</h3>
        <p>Lyra will deposit your payments here.</p>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='payment_preference'
              label='Payment Preference'
              component={SelectFieldRFF}
              options={paymentPreferenceOptions}
              placeholder='Select'
              useStringValue
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='bank'
              component={InputFieldRFF}
              label='Bank Name'
              placeholder='Bank Name'
              testID={tID('ProviderInfoForm-bankname-field')}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='routing_number'
              type='text'
              component={MaskedInputField}
              label='Routing Number'
              mask='111111111'
              placeholder='9 Digit Routing Number'
              data-test-id='ProviderInfoForm-routingnumber-field'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='account_number'
              component={InputFieldRFF}
              label='Account Number'
              placeholder='Up to 17 Digit Account Number'
              testID={tID('ProviderInfoForm-accountnumber-field')}
            />
          </div>
        </div>

        <div className={`row ${styles['submit-container']}`}>
          <div className='col-xs-12'>
            <PrimaryButton
              id='test-providerinfo-submit'
              isLoading={submitting}
              type='submit'
              // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
              onKeyDown={handleSubmit(submitFunction)}
            >
              {primaryButtonText}
            </PrimaryButton>
          </div>
        </div>
      </form>
    )
  }
}

export default ProviderInfoForm
